/**
 * GraphQL client configuration with Apollo and WebSocket support.
 *
 * @module store/graphql
 * @description Initializes and configures Apollo Client with HTTP and WebSocket links for GraphQL
 * queries, mutations, and subscriptions. Includes error handling, authentication, retry logic,
 * and real-time subscription support via WebSockets.
 *
 * @example
 * import { initGraphQL, listenGraphQLWebsocket, GRAPHQL_WEBSOCKET_STATE } from '@/store/graphql';
 *
 * // Initialize GraphQL client
 * const client = initGraphQL(true);
 *
 * // Listen to WebSocket events
 * const unsubscribe = listenGraphQLWebsocket(GRAPHQL_WEBSOCKET_STATE.CONNECTED, () => {
 *   console.log('WebSocket connected');
 * });
 */
import { SubscriptionClient } from "subscriptions-transport-ws";
import _, { chain } from "lodash";
import LocalStorageManager from "@/localStorageManager";
import { jsonParse } from "@/helpers/parser";
import { WebSocketLink } from "@apollo/client/link/ws";
import fetch from "unfetch";
import { onError } from "@apollo/client/link/error";
import { Notification } from "element-ui";
import { captureGraphQLError } from "@/sentry";
import * as Sentry from "@sentry/browser";
import { FragmentDefinitionNode, GraphQLError, OperationDefinitionNode } from "graphql";
import { getMainDefinition } from "@apollo/client/utilities";
import { setContext } from "@apollo/client/link/context";
import { getAuthType, getToken } from "@/auth";
import { RetryLink } from "@apollo/client/link/retry";
import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  split,
  createHttpLink,
} from "@apollo/client/core";
import { config } from "@/store/api";
import { formatToken } from "@/helperMethods/util";
import catchRedirect from "@/helpers/catchRedirect";

const GQL_SUBSCRIPTION_INACTIVITY_TIMEOUT = 0; // explicitly disable inactivity timeout
const GQL_SUBSCRIPTION_TIMEOUT = 20 * 1000;
const GQL_SUBSCRIPTION_MIN_TIMEOUT = 10 * 1000;

let subscriptionClient: SubscriptionClient;
const subscriptionClientEventListener: Record<string, Function[]> = {};

/**
 * GraphQL WebSocket connection states.
 */
enum GRAPHQL_WEBSOCKET_STATE {
  CONNECTING = "connecting",
  CONNECTED = "connected",
  RECONNECTING = "reconnecting",
  RECONNECTED = "reconnected",
  DISCONNECTED = "disconnected",
  ERROR = "error",
}

/**
 * WebSocket ready states mapped to numeric values.
 */
enum WEBSOCKET_READYSTATE {
  CONNECTING,
  OPEN,
  CLOSING,
  CLOSED,
}

/**
 * Registers a listener for GraphQL WebSocket events.
 *
 * @param {GRAPHQL_WEBSOCKET_STATE} eventName - WebSocket event to listen for
 * @param {Function} listener - Callback function to execute on event
 * @returns {Function} Unsubscribe function to remove the listener
 *
 * @example
 * const unsub = listenGraphQLWebsocket(GRAPHQL_WEBSOCKET_STATE.CONNECTED, () => {
 *   console.log('Connected');
 * });
 * // Remember to unsubscribe when component unmounts
 * unsub();
 */
const listenGraphQLWebsocket = (eventName: GRAPHQL_WEBSOCKET_STATE, listener: Function) => {
  const unsubFn = () => {
    _.remove(subscriptionClientEventListener[eventName], function (fn) {
      return fn === listener;
    });
  };
  if (!subscriptionClientEventListener[eventName]) {
    subscriptionClientEventListener[eventName] = [listener];
    return unsubFn;
  }
  subscriptionClientEventListener[eventName].push(listener);
  return unsubFn;
};

/**
 * Checks if the GraphQL WebSocket is connected.
 *
 * @returns {boolean} True if WebSocket is in OPEN state
 */
const isGraphQLWebsocketConnected = () => {
  return subscriptionClient.status === WEBSOCKET_READYSTATE.OPEN;
};

/**
 * Checks if the GraphQL WebSocket is connecting.
 *
 * @returns {boolean} True if WebSocket is in CONNECTING state
 */
const isGraphQLWebsocketConnecting = () => {
  return subscriptionClient.status === WEBSOCKET_READYSTATE.CONNECTING;
};

/**
 * Initializes the GraphQL WebSocket subscription client.
 *
 * @returns {{ subscriptionClient: SubscriptionClient, wsLink: WebSocketLink }}
 * Subscription client and WebSocket link
 *
 * @example
 * const { wsLink } = initGraphQLSubscription();
 */
const initGraphQLSubscription = () => {
  const wsUri = chain(new URL("subscriptions", config.root))
    .update("protocol", (protocol) => protocol.replace("http", "ws"))
    .value();

  let userProfile: any;

  const localStoreProfile = LocalStorageManager.getItem("profile");
  if (localStoreProfile) {
    userProfile = jsonParse(localStoreProfile);
  }

  console.info("WS with profile", !!userProfile);

  // See https://github.com/apollographql/subscriptions-transport-ws#constructorurl-options-websocketimpl
  subscriptionClient = new SubscriptionClient(wsUri.href, {
    connectionParams: userProfile,
    timeout: GQL_SUBSCRIPTION_TIMEOUT,
    minTimeout: GQL_SUBSCRIPTION_MIN_TIMEOUT,
    reconnect: true,
    lazy: true,
    inactivityTimeout: GQL_SUBSCRIPTION_INACTIVITY_TIMEOUT,
  });

  Object.values(GRAPHQL_WEBSOCKET_STATE).forEach((state: string) => {
    subscriptionClient.on(state, (...args) => {
      _.forEach(subscriptionClientEventListener[state], (fn: Function) => {
        fn(...args);
      });
    });
  });

  const wsLink = new WebSocketLink(subscriptionClient);
  return { subscriptionClient, wsLink };
};

/**
 * Builds the Apollo link chain with error handling, authentication, and retry logic.
 *
 * @param {boolean} withWsLink - Whether to include WebSocket link for subscriptions
 * @returns {ApolloLink[]} Array of Apollo links to chain together
 *
 * @todo For apollo-client@3.x use setLink method to reconfigure links dynamically
 */
const buildApolloLink = (withWsLink = false) => {
  const httpLink = createHttpLink({
    uri: new URL("graphql", config.root).href,
    fetch: fetch,
  });

  const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach((err) => {
        const errCode = _.get(err, "extensions.code");

        switch (errCode) {
          case "UNAUTHENTICATED":
            Notification.error({
              title: "Authentication Error",
              message: "Please refresh the page. Your token is expired",
              position: "bottom-right",
            });

            // retry the request, returning the new observable
            break;
          case "FORBIDDEN":
            // TODO: deal with the lack of permissions
            break;
          case "401_UNAUTHORIZED":
            catchRedirect({
              response: {
                status: 401,
              },
            });
            break;
          default:
        }
        captureGraphQLError(operation, err as GraphQLError);
      });
      const serverErrorWA = graphQLErrors?.[0]?.extensions?.code === "500_SERVER_ERROR_WA";
      if (serverErrorWA) {
        return; // to avoid retrying
      }
      return forward(operation);
    }

    if (networkError) {
      Sentry.captureException(new Error("Network error: " + JSON.stringify(networkError)));
      console.log("[Network error]:", networkError);
    }
  });

  // Create the subscription websocket link
  // using the ability to split links, you can send data to each link
  // depending on what kind of operation is being sent
  let graphqlLink;

  if (withWsLink) {
    const { wsLink } = initGraphQLSubscription();
    graphqlLink = split(
      // split based on operation typer
      ({ query }: { query: any }) => {
        const definition: OperationDefinitionNode | FragmentDefinitionNode =
          getMainDefinition(query);
        return definition.kind === "OperationDefinition" && definition.operation === "subscription";
      },
      wsLink,
      httpLink
    );
  } else {
    graphqlLink = httpLink;
  }

  const asyncAuthLink = setContext(async (request) => {
    return {
      headers: {
        authorization: await getToken().then(formatToken),
        "Authorization-Client": getAuthType(),
      },
    };
  });

  const retryLink = new RetryLink({
    delay: {
      initial: 300,
      max: Infinity,
      jitter: true,
    },
    attempts: (count, operation, error) => {
      return !!error && error.statusCode !== 401 && error.statusCode !== 403 && count < 5;
    },
  });

  return [asyncAuthLink, errorLink, retryLink, graphqlLink];
};

let apolloClient: any = null;

/**
 * Builds and caches the Apollo Client instance.
 *
 * @param {ApolloLink} apolloLink - Configured Apollo link chain
 * @returns {ApolloClient} Initialized Apollo Client (cached singleton)
 */
const buildApolloClient = (apolloLink: ApolloLink) => {
  if (apolloClient) {
    return apolloClient;
  }

  apolloClient = new ApolloClient({
    link: apolloLink,
    cache: new InMemoryCache({
      addTypename: false,
    }),
    connectToDevTools: false,
  });

  return apolloClient;
};

/**
 * Initializes the GraphQL Apollo Client with optional WebSocket support.
 *
 * @param {boolean} withWsLink - Enable WebSocket link for subscriptions (default: true)
 * @returns {ApolloClient} Configured Apollo Client instance
 *
 * @example
 * const client = initGraphQL(true);
 * const result = await client.query({ query: MY_QUERY });
 */
const initGraphQL = (withWsLink = true) => {
  console.info("initializing GraphQL");
  const links = buildApolloLink(withWsLink);
  const apolloLink = ApolloLink.from(links);
  return buildApolloClient(apolloLink);
};

export {
  initGraphQL,
  isGraphQLWebsocketConnecting,
  isGraphQLWebsocketConnected,
  listenGraphQLWebsocket,
  GRAPHQL_WEBSOCKET_STATE,
};
